import { useInterval, useSetState } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { BaseFillTips } from '@/app/components/icons/BaseFillTips';
import { BaseLineDown } from '@/app/components/icons/BaseLineDown';
import { SITE } from '@/app/constants';
import { useTextOverflow } from '@/app/hooks';
import { cn } from '@/app/utils';

type TipsCarouselState = {
  isTipsExpanded: boolean;
  currentTipIndex: number;
  fadeState: 'in' | 'out';
};

interface TipsCarouselProps {
  className?: string;
  isTipsVisible: boolean;
  tipsData: string[];
  intervalDuration: number;
}

/**
 * Tips 轮播组件（用于文生音频 & 专业模式）
 *
 * @param isTipsVisible 是否显示 tips
 * @param tipsData tips 数据
 * @param intervalDuration tips 轮播间隔时间（毫秒）
 */
export const TipsCarousel = ({ className, isTipsVisible, tipsData, intervalDuration }: TipsCarouselProps) => {
  const t = useTranslations();
  const [state, setState] = useSetState<TipsCarouselState>({
    isTipsExpanded: false,
    currentTipIndex: 0,
    fadeState: 'in',
  });

  useInterval(
    () => {
      setState({ fadeState: 'out' });
      setTimeout(() => {
        setState({ currentTipIndex: (state.currentTipIndex + 1) % tipsData.length, fadeState: 'in' });
      }, 300);
    },
    isTipsVisible && tipsData.length > 0 ? intervalDuration : undefined,
  );

  const currentTip = useMemo(() => {
    if (!tipsData || tipsData.length === 0) return '';
    return tipsData[state.currentTipIndex];
  }, [state.currentTipIndex, tipsData, SITE]);

  const { ref: tipTextRef, isTextOverflow } = useTextOverflow([currentTip, isTipsVisible]);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 pr-[10px] transition-all duration-300 ease-in-out',
        className,
        isTipsVisible
          ? 'max-h-full translate-y-0 transform opacity-100'
          : 'max-h-0 translate-y-2 transform overflow-hidden opacity-0',
      )}
    >
      <span className="inline-flex flex-1 select-none items-center overflow-hidden font-normal text-system-white36 text-xs leading-[1.4] transition-all duration-200 ease-in-out">
        <span className="size-[20px] flex-shrink-0 p-1">
          <BaseFillTips />
        </span>
        <span className="flex-shrink-0">{t('text2audio.timestampTextArea.tips')}</span>
        <span className="mx-2 h-3 w-[1px] flex-shrink-0 bg-system-white opacity-[.16]" />
        <span
          ref={tipTextRef}
          key={state.currentTipIndex}
          className={cn(
            'flex-1 transition-opacity duration-300 ease-in-out',
            !state.isTipsExpanded && 'truncate',
            state.fadeState === 'in' ? 'opacity-100' : 'opacity-0',
          )}
        >
          {currentTip}
        </span>
      </span>
      {isTextOverflow && (
        <BaseLineDown
          className={cn('flex-shrink-0 cursor-pointer text-base', state.isTipsExpanded && 'rotate-180')}
          onClick={() => setState({ isTipsExpanded: !state.isTipsExpanded })}
        />
      )}
    </div>
  );
};
